import { useState, useMemo, useEffect, useRef } from "react";
import { processTrackData } from "./trackListUtils";
import { UNSPOTTED_TRACK_ID } from "./constants";

/**
 * Unified hook for track car filtering (account, product, search, track).
 * Supports two modes: "manager" (full yard) and "crew" (selected + target tracks only).
 *
 * @param {Object} params
 * @param {"manager"|"crew"} params.mode - Filter mode
 * @param {Object} params.processedCarsMap - Map of trackId to array of cars
 * @param {string} [params.initialSearchTerm=null] - Initial search term (manager only)
 * @param {string} [params.viewMode="grid"] - View mode for buildVisibleCars/buildFoundCarMap (manager only)
 * @param {Object} [params.selectedTrack=null] - Selected track object (crew only)
 * @param {Object} [params.targetTrack=null] - Target track object (crew only)
 * @param {Function} [params.setSelectedShipmentIds] - Clear selection on filter change (crew only)
 * @param {Function} [params.setLastSelectedContext] - Clear selection context (crew only)
 * @param {Function} [params.setSelectedShipment] - Clear selected shipment (crew only)
 * @returns {Object} Filter state and computed values (shape depends on mode)
 */
export const useTrackDataFilters = ({
  mode,
  processedCarsMap,
  initialSearchTerm = null,
  viewMode = "grid",
  selectedTrack = null,
  targetTrack = null,
  setSelectedShipmentIds,
  setLastSelectedContext,
  setSelectedShipment,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [accountFilter, setAccountFilter] = useState(null);
  const [productFilter, setProductFilter] = useState(null);
  const [trackFilter, setTrackFilter] = useState([]);

  const accountFilterId = accountFilter?.id || null;
  const productFilterId = productFilter?.id || null;
  const productCommodityId = productFilter?.reloadYardCommodityId || null;

  const prevResultsRef = useRef(null);

  // Crew: derive trackIds from selected + target + unspotted
  const crewTrackIds = useMemo(() => {
    if (mode !== "crew") return [];
    const selectedTrackId = selectedTrack?.locations?.items?.[0]?.id;
    const targetTrackId = targetTrack?.locations?.items?.[0]?.id;
    return [selectedTrackId, targetTrackId, UNSPOTTED_TRACK_ID].filter(Boolean);
  }, [mode, selectedTrack, targetTrack]);

  // Manager: when a track filter is active, always keep the unspotted lane in
  // scope so managers can still spot unspotted railcars onto the filtered tracks.
  // (When no filter is set, leave trackFilter empty so all tracks show.)
  const managerTrackIds = useMemo(() => {
    if (mode !== "manager") return [];
    if (!trackFilter || trackFilter.length === 0) return trackFilter;
    return [...trackFilter, UNSPOTTED_TRACK_ID];
  }, [mode, trackFilter]);

  const trackIds = mode === "manager" ? managerTrackIds : crewTrackIds;

  const shouldBuildList = mode === "manager" && viewMode === "list";
  const shouldBuildFoundMap = shouldBuildList || Boolean(searchTerm);

  const { visibleCarsMap, visibleCars, foundCarMap } = useMemo(() => {
    if (!processedCarsMap && mode === "crew")
      return { visibleCarsMap: {}, visibleCars: [], foundCarMap: {} };

    const results = processTrackData(
      processedCarsMap || {},
      {
        accountId: accountFilterId,
        productId: productFilterId,
        reloadYardCommodityId: productCommodityId,
        trackIds,
        searchTerm: mode === "manager" ? searchTerm : undefined,
      },
      prevResultsRef.current,
      {
        buildVisibleCars: mode === "manager" ? shouldBuildList : false,
        buildFoundCarMap: mode === "manager" ? shouldBuildFoundMap : false,
      },
    );

    prevResultsRef.current = results;
    return results;
  }, [
    processedCarsMap,
    mode,
    accountFilterId,
    productFilterId,
    productCommodityId,
    trackIds,
    searchTerm,
    shouldBuildList,
    shouldBuildFoundMap,
  ]);

  // Crew: clear selection when account filter changes
  const prevAccountFilterIdRef = useRef(accountFilterId);
  useEffect(() => {
    if (mode !== "crew") return;
    if (
      prevAccountFilterIdRef.current !== accountFilterId &&
      prevAccountFilterIdRef.current !== null
    ) {
      if (setSelectedShipmentIds) setSelectedShipmentIds([]);
      if (setLastSelectedContext) setLastSelectedContext(null);
      if (setSelectedShipment) setSelectedShipment(null);
    }
    prevAccountFilterIdRef.current = accountFilterId;
  }, [
    mode,
    accountFilterId,
    setSelectedShipmentIds,
    setLastSelectedContext,
    setSelectedShipment,
  ]);

  if (mode === "crew") {
    const selectedTrackId = selectedTrack?.locations?.items?.[0]?.id;
    const displayProcessedCarsMap = visibleCarsMap;
    const displayProcessedCars = selectedTrackId
      ? visibleCarsMap[selectedTrackId] || []
      : [];

    return {
      accountFilter,
      setAccountFilter,
      productFilter,
      setProductFilter,
      accountFilterId,
      productFilterId,
      productCommodityId,
      displayProcessedCarsMap,
      displayProcessedCars,
    };
  }

  return {
    searchTerm,
    setSearchTerm,
    accountFilter,
    setAccountFilter,
    productFilter,
    setProductFilter,
    trackFilter,
    setTrackFilter,
    visibleCarsMap,
    visibleCars,
    foundCarMap,
    accountFilterId,
    productFilterId,
    productCommodityId,
  };
};
